"use client";

import { sdk, AuthClientInstance } from "@asosteam/asos-web-auth-client";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  forwardRef,
} from "react";
import { useStore } from "@repo/ui/store";
import { ButtonProps, Button } from "../button";

type AuthSDKConfig = Parameters<typeof sdk>[0];
type AuthContextState =
  | { status: "initialising"; authClient: null }
  | {
      status: "authenticated" | "anonymous";
      authClient: AuthClientInstance;
    };

export type AuthProviderProps = {
  children: React.ReactNode;
  clientId: string;
  scopes: string[];
  clientType: AuthSDKConfig["clientType"];
  anonymousSessionEnabled?: boolean;
};

const AuthContext = createContext<AuthContextState>({
  status: "initialising",
  authClient: null,
});

export const Provider = ({
  clientId,
  scopes,
  clientType,
  anonymousSessionEnabled = false,
  children,
}: AuthProviderProps) => {
  const [status, setStatus] =
    useState<AuthContextState["status"]>("initialising");
  const [authClient, setAuthClient] = useState<AuthClientInstance | null>(null);

  const { countryData, keyStoreDataversion } = useStore();

  useEffect(() => {
    const { authClient } = sdk({
      clientId,
      clientType,
      anonymousSessionEnabled,
      scopes,
      context: {
        store: countryData.storeCode,
        keyStoreDataversion,
        country: countryData.countryCode,
        lang: countryData.defaultLanguage,
      },
    });

    authClient.initialise().then(() => {
      try {
        // ensure we can get claims before signalling readiness.
        // this is necessary because the SDK resolves even if it fails to create a session
        authClient.getIdTokenClaims();
        setAuthClient(authClient);
        setStatus(authClient.isAuthenticated() ? "authenticated" : "anonymous");
      } catch (error) {
        console.log("Error getting ID token claims:", error);
      }
    });
  }, [
    countryData,
    keyStoreDataversion,
    clientId,
    scopes,
    clientType,
    anonymousSessionEnabled,
  ]);

  const contextValue: AuthContextState =
    status === "initialising"
      ? { status, authClient: null }
      : { status, authClient: authClient as AuthClientInstance };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

Provider.displayName = "Auth.Provider";

export const useAuth = (): AuthContextState => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

type AuthButtonProps = ButtonProps;

export const Login = forwardRef<HTMLButtonElement, AuthButtonProps>(
  ({ onClick, ...props }, ref) => {
    const { status, authClient } = useAuth();

    const redirectToLogin = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      onClick?.(event);
      authClient?.redirectToLogin();
    };

    if (status === "authenticated") {
      return null;
    }

    return <Button {...props} ref={ref} onClick={redirectToLogin} />;
  },
);

Login.displayName = "Auth.Login";

export const Logout = forwardRef<HTMLButtonElement, AuthButtonProps>(
  ({ onClick, ...props }, ref) => {
    const { status, authClient } = useAuth();

    const logout = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      onClick?.(event);
      authClient?.logout();
    };

    if (status === "authenticated") {
      return <Button {...props} ref={ref} onClick={logout} />;
    }

    return null;
  },
);

Logout.displayName = "Auth.Logout";
